import React, { useEffect, useMemo, useRef } from "react";
import { Button, DatePicker as DatePickerPD } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  IRegMenReportFilters,
  useAvailableRegionalManagers,
} from "~/entity/RegMenReports";
import { onScrollBottomDealers } from "~/hooks/selectDealerValue";
import {
  DealersLocationItem,
  DealerStatus,
  useGetDealersLocationItemQuery,
} from "~/schemas/generated/graphql";
import { mapToOptions } from "~/widgets/Filters/FilterSummary/helper";

import SelectField from "./ui/SelectField";
import {
  FilterCategory,
  getAssignmentOptions,
  getCategoryOptions,
  getDefaultValues,
  getQuarterOptions,
  getYearOptions,
  OptionItem,
  parsePeriodFilters,
  PeriodFilterValues,
} from "./lib";

import s from "./s.less";

interface PeriodFilterProps {
  canView: boolean;
  canDownload: boolean;
  onApply: (filters: IRegMenReportFilters) => void;
  onGenerateFile: (filters: IRegMenReportFilters) => void;
  onReset: () => void;
}

const handleScrollBottom =
  (callback: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
      callback();
    }
  };

const PeriodFilter = ({
  canView,
  canDownload,
  onApply,
  onGenerateFile,
  onReset,
}: PeriodFilterProps) => {
  const { t } = useTranslation();
  const isEn = localStorage.getItem("language") === "en";

  const { dealersForSelect, introspectionHandler, searchDealer } =
    onScrollBottomDealers(DealerStatus.Active);
  const { regMenOptions, onScrollBottomRegMen, searchRegMen } =
    useAvailableRegionalManagers();

  const dealerCity = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem.City },
  });
  const dealerSubject = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem.Subject },
  });
  const dealerFO = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem.Fo },
  });
  const dealerCountry = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem.Country },
  });

  const optionsByCategory = useMemo<Record<FilterCategory, OptionItem[]>>(
    () => ({
      dealer: dealersForSelect as OptionItem[],
      city: mapToOptions(dealerCity, isEn),
      FO: mapToOptions(dealerFO, isEn),
      subject: mapToOptions(dealerSubject, isEn),
      country: mapToOptions(dealerCountry, isEn),
    }),
    [dealersForSelect, dealerCity, dealerFO, dealerSubject, dealerCountry],
  );

  const { control, watch, handleSubmit, setValue, reset, getValues } =
    useForm<PeriodFilterValues>({
      defaultValues: getDefaultValues(t),
    });

  const selectedCategory = watch("selectFrom");
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setValue("filterValues", []);
  }, [selectedCategory?.value, setValue]);

  const category = (selectedCategory?.value as FilterCategory) || "dealer";
  const isDealerCategory = category === "dealer";

  const onSubmitForm = (data: PeriodFilterValues) => {
    onApply(parsePeriodFilters(data));
  };

  const handleGenerateFile = () => {
    onGenerateFile(parsePeriodFilters(getValues()));
  };

  const resetFilter = () => {
    reset(getDefaultValues(t));
    onReset();
  };

  return (
    <div className={s.wrapper_period_filter}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className={s.fields}>
          <div className={s.rowMain}>
            <SelectField
              name="selectFrom"
              control={control}
              label={t("regMen.filterBy")}
              options={getCategoryOptions(t)}
            />
            <SelectField
              name="filterValues"
              control={control}
              label={t("regMen.filterValuesLabel")}
              options={optionsByCategory[category] ?? []}
              mode="multiple"
              showSearch
              onSearch={isDealerCategory ? searchDealer : undefined}
              onPopupScroll={
                isDealerCategory
                  ? handleScrollBottom(introspectionHandler)
                  : undefined
              }
            />
            <SelectField
              name="regionalManager"
              control={control}
              label={t("regMen.regionalManager")}
              options={regMenOptions}
              showSearch
              allowClear
              onSearch={searchRegMen}
              onPopupScroll={handleScrollBottom(onScrollBottomRegMen)}
            />
            <SelectField
              name="assignmentType"
              control={control}
              label={t("regMen.assignmentType")}
              options={getAssignmentOptions(t)}
              allowClear
            />
          </div>
          <div className={s.rowSecondary}>
            <div className={`${s.field} ${s.actualOn}`}>
              <span>{t("regMen.actualOn")}</span>
              <Controller
                name="actualOn"
                control={control}
                render={({ onChange, value, onBlur }) => (
                  <DatePickerPD
                    placeholder="дд.мм.гггг"
                    format="DD.MM.YYYY"
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <SelectField
              name="quarter"
              control={control}
              label={t("regMen.quarter")}
              options={getQuarterOptions()}
            />
            <SelectField
              name="year"
              control={control}
              label={t("regMen.year")}
              options={getYearOptions()}
            />
          </div>
        </div>
        <div className={s.buttons}>
          <Button
            color={"secondary"}
            variant={"outlined"}
            size="middle"
            fontSize="12"
            onClick={resetFilter}
            role="button"
            aria-label={t("regMen.resetFilters")}
          >
            <span>{t("regMen.resetFilters")}</span>
          </Button>
          {canDownload && (
            <Button
              color="secondary"
              size="middle"
              fontSize="12"
              htmlType="button"
              onClick={handleGenerateFile}
            >
              {t("editorQ.save_excel")}
            </Button>
          )}
          {canView && (
            <Button
              color="primary"
              size="middle"
              fontSize="12"
              htmlType="submit"
            >
              {t("regMen.btnTable")}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PeriodFilter;
